// Shared types, constants and helpers for the Posteingang (inbox) redesign.
// Pure/presentational utilities used by InboxPage and the extracted inbox components.

import React from 'react';
import { Reply, Clock, CheckCircle2, Inbox as InboxIcon, type LucideIcon } from 'lucide-react';
import { formatDateToDDMMYYYY } from '../../utils/date';
import { inboxThreadKey } from '../../utils/inboxRoutes';
import { normalizeMailBody } from '../../utils/mailBody';

// ---- Types ----

export {
  NYLAS_MAX_ATTACHMENTS_TOTAL_BYTES as NYLAS_MAX_ATTACHMENT_BYTES,
  NYLAS_MAX_ATTACHMENTS_TOTAL_MB as NYLAS_MAX_ATTACHMENT_MB,
  NYLAS_MAX_ATTACHMENTS_COUNT,
  estimateNylasOutgoingBodyBytes,
  mergeNylasAttachmentFiles,
  validateNylasAttachments,
} from '../../utils/nylasSendLimits';
export interface InboxAccount {
  id: string | number;
  email: string;
  provider: string;
  nylas_grant_id?: string | null;
  signature_html?: string | null;
  is_private?: boolean;
  owner_user_id?: string | null;
  incoming_invoice_ingest?: boolean;
  /** on_done: KI merkt den Ordner nur vor, verschoben wird bei „Erledigt“. */
  filing_mode?: InboxFilingMode;
  created_at: string;
  sync_since?: string | number | null;
}

export type InboxFilingMode = 'immediate' | 'on_done';

export type ChatAttachment = { id?: string | null; filename: string; size?: number | null; content_type?: string | null };
export type MailClassification = 'spam' | 'offen' | 'waiting_for_reply' | 'done' | 'other' | null;
export type UrgencyLevel = 'low' | 'normal' | 'high' | null;

export interface InboxEmail {
  id: string | number;
  account_id: string | number;
  external_message_id?: string | null;
  thread_id?: string | null;
  from_addr: string;
  to_addr: string;
  subject: string;
  date_at: string | null;
  snippet: string;
  body_text?: string;
  body_html?: string;
  attachments_json?: string | ChatAttachment[] | null;
  /** Nach `parseEmailsWithAttachments` gesetzt (aus attachments_json). */
  attachments?: ChatAttachment[];
  status?: MailStatus; // legacy alias from API
  lead_id?: string | number | null;
  lead_display_id?: string | null;
  lead_title?: string | null;
  /** Zuständiger Web-Nutzer (Profil-ID). */
  assigned_user_id?: string | null;
  classification?: MailClassification;
  classification_confidence?: number | null;
  needs_reply?: boolean | null;
  urgency_level?: UrgencyLevel;
  is_urgent?: boolean | null;
  ai_last_evaluated_at?: string | null;
  /** Anzahl Nachrichten im Thread (Server-Liste). */
  thread_count?: number;
  /** Ob Anhänge existieren, ohne attachments_json zu laden. */
  has_attachments?: boolean;
  /** Kurze Dateinamen für die Listenansicht. */
  attachment_names?: string[];
  starred?: boolean;
  is_unread?: boolean;
  provider_folder_ids?: string[];
  /** Von der KI vorgemerkter Ordner; die Mail wandert erst bei „Erledigt“ hinein. */
  pending_folder_id?: string | null;
  opened_count?: number;
  opened_at?: string | null;
  link_click_count?: number;
}

export type ChatMessage = {
  id: string;
  from: 'me' | 'them';
  /** Absenderzeile dieser Nachricht (`Name <mail>`), nicht die des Threads. */
  senderAddr?: string;
  text: string;
  created_at: string;
  html?: string;
  /** Quoted history for Gmail-style expand/collapse in the conversation view. */
  quotedText?: string | null;
  quotedHtml?: string | null;
  attachments?: ChatAttachment[];
  external_message_id?: string | null;
  account_id?: string | number;
};

export type MailStatus = 'spam' | 'offen' | 'waiting_for_reply' | 'done' | 'other';
export const MAIL_STATUSES: MailStatus[] = ['offen', 'waiting_for_reply', 'done', 'other', 'spam'];
/** Workflow-Status im Postfach. Spam ist eine Markierung und fehlt hier absichtlich. */
export const MAILBOX_STATUSES: MailStatus[] = ['offen', 'waiting_for_reply', 'done', 'other'];
export const INBOX_EMAIL_DND_TYPE = 'application/x-meister-inbox-email-ids';

export function parseInboxEmailDragIds(dataTransfer: DataTransfer): string[] {
  const raw =
    dataTransfer.getData(INBOX_EMAIL_DND_TYPE) ||
    dataTransfer.getData('text/plain');
  if (!raw.trim()) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.map((id) => String(id).trim()).filter(Boolean);
    }
  } catch {
    /* comma-separated fallback */
  }
  return raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

export function inboxEmailDragHasIds(dataTransfer: DataTransfer): boolean {
  const types = Array.from(dataTransfer.types);
  return types.includes(INBOX_EMAIL_DND_TYPE) || types.includes('text/plain');
}

export type MailFilter = 'Alle' | MailStatus;

export function getEffectiveMailStatus(mail: Pick<InboxEmail, 'classification'>): MailStatus {
  const c = mail.classification;
  if (c && (MAIL_STATUSES as readonly string[]).includes(c)) return c;
  return 'other';
}

/** Mail that "Erledigt" moved into its pending folder (PATCH /classification → filed). */
export type FiledOnDone = { id: string; folder_external_id: string };

export function parseFiledOnDone(raw: unknown): FiledOnDone[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => ({
      id: String((entry as { id?: unknown })?.id ?? ''),
      folder_external_id: String((entry as { folder_external_id?: unknown })?.folder_external_id ?? ''),
    }))
    .filter((entry) => entry.id && entry.folder_external_id);
}

export interface LinkedActivityTask {
  id: string;
  reason: string | null;
  confidence: number | null;
  created_by_ai: boolean;
  created_at: string;
  activity: {
    id: string;
    lead_id: string | null;
    title: string;
    activity_type: string;
    due_at: string | null;
    priority: string | null;
    notes: string | null;
    done: boolean;
    assignee_users?: { user_id: string; display_name?: string | null; email?: string | null }[];
    assignee_employees?: { id: string; first_name?: string; last_name?: string }[];
  };
}

export type InboxLeadContact = {
  id?: string | number;
  name?: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string;
  phone?: string;
  address?: string;
  contact_type?: string | null;
  contact_kind?: string | null;
};

/** Activity row from GET /api/leads/:id/activities — inbox sidebar. */
export type InboxLeadActivity = {
  id: string;
  lead_id?: string | number | null;
  title: string;
  activity_type: string;
  due_at: string | null;
  end_at?: string | null;
  done: boolean;
  priority?: string | null;
  notes?: string | null;
  assignee_users?: { display_name?: string | null; email?: string | null }[];
  assignee_employees?: { first_name?: string; last_name?: string }[];
};

/** Lead row from GET /api/leads — used for sidebar Projekt/Kontakt cards. */
export type LeadOption = {
  id: string | number;
  display_id: string;
  name: string;
  status?: string;
  category?: string;
  lead_title?: string;
  created_at?: string;
  net_total?: number | null;
  site_address?: string | null;
  address?: string;
  email?: string;
  phone?: string;
  contact_id?: string | number | null;
  contact?: InboxLeadContact | null;
  /** Kunden-Bestellnummer / Auftragskennzeichen (Baustelle). */
  bestellnummer?: string | null;
  /** Anzeigename des Baustellenkontakts. */
  site_contact_name?: string | null;
};

/** Map GET /api/leads row → inbox LeadOption (inkl. Bestellnummer / Baustellenkontakt). */
export function mapApiLeadToInboxLeadOption(l: {
  id?: string | number;
  display_id?: string | null;
  name?: string | null;
  status?: string;
  category?: string;
  lead_title?: string | null;
  created_at?: string;
  net_total?: number | null;
  site_address?: string | null;
  address?: string;
  email?: string;
  phone?: string;
  contact_id?: string | number | null;
  contact?: InboxLeadContact | null;
  baustelle?: {
    bestellnummer?: string | null;
    contact?: InboxLeadContact | null;
  } | null;
  bestellnummer?: string | null;
  site_contact_name?: string | null;
}): LeadOption {
  const siteContact = l.baustelle?.contact ?? null;
  const siteName = [siteContact?.first_name, siteContact?.last_name]
    .map((p) => String(p ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  return {
    id: l.id as string | number,
    display_id: l.display_id || `#${l.id}`,
    name: l.name || '',
    status: l.status,
    category: l.category,
    lead_title: l.lead_title ?? undefined,
    created_at: l.created_at,
    net_total: l.net_total ?? null,
    site_address: l.site_address ?? null,
    address: l.address,
    email: l.email,
    phone: l.phone,
    contact_id: l.contact_id ?? l.contact?.id ?? null,
    contact: l.contact ?? null,
    bestellnummer:
      l.baustelle?.bestellnummer != null
        ? String(l.baustelle.bestellnummer).trim() || null
        : l.bestellnummer != null
          ? String(l.bestellnummer).trim() || null
          : null,
    site_contact_name:
      siteName ||
      String(siteContact?.name ?? '').trim() ||
      (l.site_contact_name != null ? String(l.site_contact_name).trim() || null : null),
  };
}

export type DirectionTab = 'alle' | 'in' | 'out';
export type SortMode = 'prio' | 'datum';
export type TriageBucket = 'antwort' | 'warten' | 'erledigt' | 'sonstiges';

// ---- Classification labels / styles ----
export function getClassificationLabel(status: MailStatus): string {
  if (status === 'offen') return 'Offen';
  if (status === 'waiting_for_reply') return 'Warten auf Rückmeldung';
  if (status === 'done') return 'Erledigt';
  if (status === 'spam') return 'Spam';
  return 'Sonstiges';
}

/** Gleiche Farbpunkte wie in der Status-Leiste. */
export const MAIL_STATUS_DOT: Record<MailStatus, string> = {
  offen: 'bg-pipedrive-blue',
  done: 'bg-emerald-500',
  waiting_for_reply: 'bg-violet-500',
  spam: 'bg-rose-500',
  other: 'bg-slate-300',
};

export function getStatusStyles(status: MailStatus): { card: string; count: string; badge: string } {
  if (status === 'offen') return { card: 'bg-blue-50 border-blue-200', count: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' };
  if (status === 'waiting_for_reply') return { card: 'bg-violet-50 border-violet-200', count: 'text-violet-700', badge: 'bg-violet-100 text-violet-700' };
  if (status === 'spam') return { card: 'bg-rose-50 border-rose-200', count: 'text-rose-700', badge: 'bg-rose-100 text-rose-700' };
  if (status === 'done') return { card: 'bg-emerald-50 border-emerald-200', count: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' };
  return { card: 'bg-app-surface-muted border-app-border', count: 'text-app-text-muted', badge: 'bg-slate-200 text-app-text-muted' };
}

export function getUrgencyLabel(urgency?: UrgencyLevel): string {
  if (urgency === 'high') return 'Dringlichkeit: hoch';
  if (urgency === 'normal') return 'Dringlichkeit: normal';
  if (urgency === 'low') return 'Dringlichkeit: niedrig';
  return '';
}

export function isUrgent(mail: Pick<InboxEmail, 'is_urgent' | 'urgency_level'>): boolean {
  return mail.is_urgent === true || mail.urgency_level === 'high';
}

// ---- Triage (KI-Priorität) ----
export const TRIAGE_SECTIONS: { key: TriageBucket; label: string; icon: LucideIcon; color: string; dot: string }[] = [
  { key: 'antwort', label: 'Braucht Antwort', icon: Reply, color: 'text-rose-600', dot: 'bg-rose-500' },
  { key: 'warten', label: 'Warten auf Rückmeldung', icon: Clock, color: 'text-violet-600', dot: 'bg-violet-500' },
  { key: 'erledigt', label: 'Erledigt', icon: CheckCircle2, color: 'text-emerald-600', dot: 'bg-emerald-500' },
  { key: 'sonstiges', label: 'Sonstiges', icon: InboxIcon, color: 'text-app-text-subtle', dot: 'bg-slate-400' },
];

export function getTriageBucket(mail: Pick<InboxEmail, 'classification' | 'needs_reply'>): TriageBucket {
  const c = mail.classification;
  if (c === 'done') return 'erledigt';
  if (c === 'waiting_for_reply') return 'warten';
  if (mail.needs_reply === true || c === 'offen') return 'antwort';
  return 'sonstiges';
}

const TRIAGE_RANK: Record<TriageBucket, number> = { antwort: 0, warten: 1, erledigt: 2, sonstiges: 3 };
export function triageRank(mail: InboxEmail): number {
  return TRIAGE_RANK[getTriageBucket(mail)];
}

/** Mirrors `inbox_sort_rank(..., 'prio', ...)`: bucket * 2, urgent first within bucket. */
export function inboxPrioSortRank(
  mail: Pick<InboxEmail, 'classification' | 'needs_reply' | 'is_urgent' | 'urgency_level'>,
): number {
  return TRIAGE_RANK[getTriageBucket(mail)] * 2 + (isUrgent(mail) ? 0 : 1);
}

// ---- Direction ----
export function getDirection(mail: Pick<InboxEmail, 'from_addr'>, accountEmail: string): 'in' | 'out' {
  const a = (accountEmail || '').toLowerCase();
  const f = (mail.from_addr || '').toLowerCase();
  return a && f.includes(a) ? 'out' : 'in';
}

/** External party address for display and reply context (recipient on sent, sender on received). */
export function getCounterpartyAddr(
  mail: Pick<InboxEmail, 'from_addr' | 'to_addr'>,
  accountEmail: string,
): string {
  const isOut = getDirection(mail, accountEmail) === 'out';
  const raw = (isOut ? mail.to_addr : mail.from_addr) || '';
  const stripped = raw.split(/\s*\|\s*cc:/i)[0].trim();
  return stripped || mail.from_addr || mail.to_addr || '';
}

/** Optional CC recipients encoded as `to | cc: a@b, c@d` on inbox rows. */
export function getCcRecipients(mail: Pick<InboxEmail, 'to_addr'>): string[] {
  const raw = String(mail.to_addr || '');
  const match = raw.match(/\|\s*cc:\s*(.+)$/i);
  if (!match?.[1]) return [];
  return match[1]
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Keep stored CC when a Nylas resync/list payload omits it. */
export function mergeInboxToAddrPreservingCc(
  existingTo: string,
  incomingTo: string,
  options?: { providerCcSpecified?: boolean },
): string {
  const existingCc = getCcRecipients({ to_addr: existingTo });
  if (existingCc.length === 0) return incomingTo;
  if (/\|\s*cc:/i.test(incomingTo)) return incomingTo;
  if (options?.providerCcSpecified) return incomingTo;
  const base = String(incomingTo || '').trim();
  return base ? `${base} | cc: ${existingCc.join(', ')}` : `| cc: ${existingCc.join(', ')}`;
}

export function parseInboxToRecipientEmails(toAddr: string): string[] {
  const raw = String(toAddr || '').split(/\s*\|\s*cc:/i)[0].trim();
  if (!raw) return [];
  const seen = new Set<string>();
  return raw
    .split(/[,;]/)
    .map((part) => extractEmail(part).toLowerCase())
    .filter((email) => {
      if (!email || !email.includes('@') || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

export function getInboxReplyRecipient(
  mail: Pick<InboxEmail, 'from_addr' | 'to_addr'>,
  accountEmail: string,
): string {
  const from = String(mail.from_addr || '').trim();
  const toEmails = parseInboxToRecipientEmails(String(mail.to_addr || ''));
  if (isOutgoingFromAccount(from, accountEmail)) {
    return toEmails[0] || extractEmail(String(mail.to_addr || ''));
  }
  return extractEmail(from) || toEmails[0] || '';
}

export function collectInboxParticipantEmails(
  mail: Pick<InboxEmail, 'from_addr' | 'to_addr'>,
  accountEmail: string,
): string[] {
  const own = accountEmail.toLowerCase().trim();
  const seen = new Set<string>();
  return [extractEmail(mail.from_addr).toLowerCase(), ...parseInboxToRecipientEmails(mail.to_addr), ...getCcRecipients(mail).map((cc) => extractEmail(cc).toLowerCase())]
    .filter((email) => {
      if (!email || email === own || seen.has(email)) return false;
      seen.add(email);
      return true;
    });
}

export function shouldOfferReplyAll(
  mail: Pick<InboxEmail, 'from_addr' | 'to_addr'>,
  accountEmail: string,
): boolean {
  return collectInboxParticipantEmails(mail, accountEmail).length > 1;
}

export function buildReplyAllCc(
  mail: Pick<InboxEmail, 'from_addr' | 'to_addr'>,
  accountEmail: string,
): string {
  const replyRecipient = getInboxReplyRecipient(mail, accountEmail).toLowerCase();
  return collectInboxParticipantEmails(mail, accountEmail)
    .filter((email) => email !== replyRecipient)
    .join(', ');
}

/** Avoid sending stale reply-all Cc when mode switches before React state updates. */
export function resolveInboxReplyCc(
  replyAll: boolean,
  replyCc: string,
  replyAllCc: string,
): string {
  const trimmed = replyCc.trim();
  if (replyAll) return (trimmed || replyAllCc).trim();
  return trimmed === replyAllCc ? '' : trimmed;
}

// ---- Contact display ----
export function displayNameFromAddr(addr?: string | null): string {
  if (!addr) return 'Unbekannt';
  const named = addr.match(/^\s*"?([^"<]+?)"?\s*<[^>]+>\s*$/);
  if (named && named[1].trim()) return named[1].trim();
  const email = extractEmail(addr);
  const local = email.split('@')[0] || email;
  return local
    .replace(/[._-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim() || email;
}

export function extractEmail(addr?: string | null): string {
  if (!addr) return '';
  const m = addr.match(/<([^>]+)>/);
  return (m ? m[1] : addr).trim();
}

/** Collapse body text for duplicate detection between persisted sends. */
export function collapseMailBodyForMatch(text: string): string {
  return normalizeMailBody(text).replace(/\s+/g, ' ').trim().toLowerCase();
}

function mailBodyPlainForMatch(mail: Pick<InboxEmail, 'body_text' | 'snippet' | 'body_html'>): string {
  const fromText = String(mail.body_text || mail.snippet || '').trim();
  if (fromText) return fromText;
  const html = String(mail.body_html || '').trim();
  if (!html) return '';
  return html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n').replace(/<[^>]+>/g, ' ');
}

export function isOutgoingFromAccount(fromAddr: string | null | undefined, accountEmail: string): boolean {
  const account = accountEmail.toLowerCase().trim();
  if (!account) return false;
  const from = extractEmail(fromAddr).toLowerCase();
  if (from && from === account) return true;
  return String(fromAddr || '').toLowerCase().includes(account);
}

function mailContentScore(mail: Pick<InboxEmail, 'body_html' | 'body_text' | 'snippet'>): number {
  return (mail.body_html?.trim() ? 4 : 0) + (mail.body_text?.length ?? 0) + (mail.snippet?.length ?? 0);
}

function mergeDuplicateThreadMail(preferred: InboxEmail, other: InboxEmail): InboxEmail {
  const attachments =
    (preferred.attachments?.length ?? 0) > 0
      ? preferred.attachments
      : (other.attachments?.length ?? 0) > 0
        ? other.attachments
        : preferred.attachments;
  return attachments !== preferred.attachments ? { ...preferred, attachments } : preferred;
}

export function isPendingSentInboxEmail(mail: Pick<InboxEmail, 'id'>): boolean {
  return String(mail.id).startsWith('pending-sent-');
}

function persistedReplacesPendingSent(
  fetched: Pick<InboxEmail, 'from_addr' | 'thread_id' | 'body_text' | 'snippet' | 'body_html'>,
  pending: Pick<InboxEmail, 'thread_id' | 'body_text' | 'snippet'>,
  accountEmail: string,
): boolean {
  if (!isOutgoingFromAccount(fetched.from_addr, accountEmail)) return false;
  if (pending.thread_id && fetched.thread_id !== pending.thread_id) return false;
  const pendingNorm = collapseMailBodyForMatch(pending.body_text || pending.snippet || '');
  const fetchedNorm = collapseMailBodyForMatch(mailBodyPlainForMatch(fetched));
  if (!pendingNorm || !fetchedNorm) return false;
  if (pendingNorm.length >= 8) {
    return fetchedNorm.includes(pendingNorm.slice(0, Math.min(120, pendingNorm.length)));
  }
  return fetchedNorm.startsWith(pendingNorm);
}

function inboxDateMs(d: string | null | undefined): number {
  return d ? new Date(d).getTime() : 0;
}

/** Merge a freshly sent row into a message list (upsert by id or external_message_id). */
export function mergeInboxEmailRow(prev: InboxEmail[], incoming: InboxEmail): InboxEmail[] {
  const ext = incoming.external_message_id;
  const idx = prev.findIndex(
    (m) =>
      (ext && m.external_message_id && String(m.external_message_id) === String(ext)) ||
      String(m.id) === String(incoming.id),
  );
  if (idx >= 0) {
    const next = [...prev];
    const merged = { ...next[idx], ...incoming };
    if (incoming.to_addr != null) {
      merged.to_addr = mergeInboxToAddrPreservingCc(next[idx].to_addr, String(incoming.to_addr));
    }
    next[idx] = merged;
    return next;
  }
  return [incoming, ...prev];
}

/**
 * Merge into the thread-grouped inbox list. One row per thread: a new message in an
 * existing thread replaces the representative when newer and bumps thread_count.
 */
export function mergeInboxThreadListRow(prev: InboxEmail[], incoming: InboxEmail): InboxEmail[] {
  const incomingKey = inboxThreadKey(incoming);
  const idx = prev.findIndex((m) => inboxThreadKey(m) === incomingKey);
  if (idx < 0) return [incoming, ...prev];

  const existing = prev[idx];
  const sameMessage =
    String(existing.id) === String(incoming.id) ||
    Boolean(
      existing.external_message_id &&
        incoming.external_message_id &&
        String(existing.external_message_id) === String(incoming.external_message_id),
    );
  const newer = inboxDateMs(incoming.date_at) >= inboxDateMs(existing.date_at);
  const threadCount = (existing.thread_count || 1) + (sameMessage ? 0 : 1);
  const next = [...prev];
  next[idx] = newer
    ? {
        ...existing,
        ...incoming,
        thread_count: threadCount,
        body_html: incoming.body_html ?? existing.body_html,
        body_text: incoming.body_text ?? existing.body_text,
      }
    : { ...existing, thread_count: threadCount };
  return next;
}

export function localInboxThreadId(anchorMailId: string | number): string {
  return `local-thread-${anchorMailId}`;
}

/** All inbox rows that belong to the same conversation as the anchor mail. */
export function filterEmailsInThread(
  anchor: Pick<InboxEmail, 'id' | 'thread_id'>,
  emails: InboxEmail[],
): InboxEmail[] {
  const threadId = anchor.thread_id;
  const localThreadId = localInboxThreadId(anchor.id);
  if (threadId) {
    return emails.filter((m) => m.thread_id === threadId || String(m.id) === String(anchor.id));
  }
  return emails.filter(
    (m) => String(m.id) === String(anchor.id) || m.thread_id === localThreadId,
  );
}

/** Keep client-only pending-sent rows until the server list contains the real message. */
export function mergeFetchedInboxEmails(
  prev: InboxEmail[],
  fetched: InboxEmail[],
  accountEmail: string,
  accountId?: string | number | null,
): InboxEmail[] {
  const scopedPrev =
    accountId != null ? prev.filter((m) => String(m.account_id) === String(accountId)) : prev;
  const pending = scopedPrev.filter(isPendingSentInboxEmail);
  const stillPending = pending.filter(
    (p) => !fetched.some((n) => persistedReplacesPendingSent(n, p, accountEmail)),
  );
  // The server already returns the list in display order, so it stays the base
  // and only client-only rows get prepended.
  let merged: InboxEmail[] = [...fetched];
  const now = Date.now();
  const recentSentKeepMs = 5 * 60 * 1000;
  const clientOnly: InboxEmail[] = [];
  for (const row of scopedPrev) {
    if (isPendingSentInboxEmail(row)) continue;
    if (
      fetched.some(
        (f) =>
          (row.external_message_id &&
            f.external_message_id &&
            String(row.external_message_id) === String(f.external_message_id)) ||
          String(f.id) === String(row.id),
      )
    ) {
      continue;
    }
    if (!isOutgoingFromAccount(row.from_addr, accountEmail)) continue;
    const age = row.date_at ? now - new Date(row.date_at).getTime() : Number.POSITIVE_INFINITY;
    if (age > recentSentKeepMs) continue;
    clientOnly.push(row);
  }
  for (const row of [...clientOnly, ...stillPending]) {
    merged = mergeInboxEmailRow(merged, row);
  }
  return merged;
}

/** Drop duplicate outgoing rows (e.g. local save without Nylas id + later sync). Prefer richer body (signature). */
export function dedupeThreadMails(mails: InboxEmail[], accountEmail: string): InboxEmail[] {
  const out: InboxEmail[] = [];
  for (const m of mails) {
    const dupIdx = out.findIndex((existing) => {
      if (
        m.external_message_id &&
        existing.external_message_id &&
        String(m.external_message_id) === String(existing.external_message_id)
      ) {
        return true;
      }
      if (
        m.external_message_id &&
        existing.external_message_id &&
        String(m.external_message_id) !== String(existing.external_message_id)
      ) {
        return false;
      }
      if (!isOutgoingFromAccount(m.from_addr, accountEmail) || !isOutgoingFromAccount(existing.from_addr, accountEmail)) {
        return false;
      }
      const a = collapseMailBodyForMatch(mailBodyPlainForMatch(m));
      const b = collapseMailBodyForMatch(mailBodyPlainForMatch(existing));
      if (!a || !b) return false;
      const shorter = a.length <= b.length ? a : b;
      const longer = a.length <= b.length ? b : a;
      const probe = shorter.slice(0, Math.min(80, shorter.length));
      return probe.length >= 16 && longer.includes(probe);
    });
    if (dupIdx >= 0) {
      const existing = out[dupIdx];
      const preferred = mailContentScore(m) > mailContentScore(existing) ? m : existing;
      const other = preferred === m ? existing : m;
      out[dupIdx] = mergeDuplicateThreadMail(preferred, other);
    } else {
      out.push(m);
    }
  }
  return out;
}

export function initialsOf(nameOrAddr?: string | null): string {
  const name = displayNameFromAddr(nameOrAddr);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Pastell-Avatare wie Kontakte/Organisationen — dezente Hintergründe, dunklere Initialen. */
const INBOX_AVATAR_PALETTE: { bg: string; text: string }[] = [
  { bg: 'bg-app-surface-muted', text: 'text-app-text-muted' },
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  { bg: 'bg-amber-100', text: 'text-amber-800' },
  { bg: 'bg-sky-100', text: 'text-sky-700' },
  { bg: 'bg-rose-100', text: 'text-rose-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h);
}

/** Message meta: time only today, otherwise „TT.MM.JJJJ, HH:mm“ (wie Prototyp). */
export function formatMessageDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return time;
  const date = formatDateToDDMMYYYY(iso.slice(0, 10));
  return `${date}, ${time}`;
}

export function inboxAvatarPair(s?: string | null): { bg: string; text: string } {
  const key = (s || '').toLowerCase().trim();
  if (!key) return INBOX_AVATAR_PALETTE[0];
  return INBOX_AVATAR_PALETTE[hashString(key) % INBOX_AVATAR_PALETTE.length];
}

// ---- Small presentational helper ----
export function Avatar({ nameOrAddr, size = 38, className = '' }: { nameOrAddr?: string | null; size?: number; className?: string }) {
  const { bg, text } = inboxAvatarPair(nameOrAddr);
  const fontWeight = size >= 32 ? 'font-semibold' : 'font-medium';
  return (
    <div
      className={`flex items-center justify-center rounded-full shrink-0 ${bg} ${text} ${fontWeight} ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.34)) }}
    >
      {initialsOf(nameOrAddr)}
    </div>
  );
}
